import React from 'react';
import { Card, Typography, Skeleton, Tag, Collapse, Space, Descriptions } from 'antd';
import { useTranslation } from 'react-i18next';
import { Workflow } from '../types/workflow.types';
import { formatDate } from '../../../../../utils/date.util';
import RenderDetails, { DetailItem } from '../../../../common/RenderDetails';
import { requiresSelector } from '@const/workflow.constants';
import { acronyms } from '@const/get-allowed-application-keys.const';
import { WORKFLOW_CONDITION_SKIP_RE_LOGIN } from '@const/workflow-condition-keys.const';

// Format camelCase to Title Case (e.g., "passportNumber" -> "Passport Number")
const formatLabel = (key: string): string => {
  // Convert camelCase to Title Case with proper spacing
  const formatted = key
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .split(' ')
    .map((word) => {
      const lowerWord = word.toLowerCase();
      // If word is an acronym, return uppercase
      if (acronyms.includes(lowerWord)) {
        return lowerWord.toUpperCase();
      }
      // Otherwise capitalize first letter
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');

  return formatted.trim();
};

// Format snake_case workflow condition keys to a readable label (e.g., "skip_re_login" -> "Skip Re Login")
const formatConditionLabel = (key: string): string => {
  return key
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const { Title } = Typography;
const { Panel } = Collapse;

interface WorkflowDetailsComponentProps {
  data: Workflow | undefined;
  success: boolean;
  isLoading: boolean;
  error: Error | null;
}

const WorkflowDetailsComponent: React.FC<WorkflowDetailsComponentProps> = ({ data, isLoading }) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Card className="shadow-lg">
        <Skeleton active />
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  const details: DetailItem[] = [
    {
      label: t('workflow.name.label', { defaultValue: 'Workflow Name' }),
      value: data.name,
    },
    {
      label: t('workflow.totalSteps.label', { defaultValue: 'Total Steps' }),
      value: data.steps.length.toString(),
    },
    {
      label: t('common.createdAt.label'),
      value: formatDate(data.createdAt),
    },
    {
      label: t('common.updatedAt.label'),
      value: formatDate(data.updatedAt),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        {/* <Title level={4}>Workflow Information</Title> */}
        <RenderDetails data={details} columns={2} />
      </div>

      <div style={{ marginTop: 24 }}>
        <Title level={4}>{t('workflow.steps.label')}</Title>
        <Collapse accordion>
          {data.steps.map((step, index) => {
            const displayName =
              step.stepName && step.stepName.trim() ? step.stepName : step.step.type.toUpperCase();
            return (
              <Panel
                header={
                  <Space>
                    <Tag color="blue">Step {step.order}</Tag>
                    <span>{displayName}</span>
                  </Space>
                }
                style={{ backgroundColor: 'white' }}
                key={index}
              >
                <RenderDetails
                  data={[
                    {
                      label: 'Step Name',
                      value: step.stepName || 'N/A',
                    },
                    {
                      label: 'Type',
                      value: step.step.type,
                    },
                    ...(step.step.find && requiresSelector(step.step.type)
                      ? [
                          {
                            label: 'Find By',
                            value: step.step.find.by || 'N/A',
                          },
                          {
                            label: 'Selector Value',
                            value: step.step.find.value || 'N/A',
                          },
                        ]
                      : []),
                    {
                      label: 'Delay (ms)',
                      value: step.delayMs?.toString() || 'N/A',
                    },
                    ...(step.step.waitForPageLoad
                      ? [
                          {
                            label: 'Wait for Page Load',
                            value: step.step.waitForPageLoad,
                          },
                        ]
                      : []),
                    ...(step.step.waitForSelector
                      ? [
                          {
                            label: 'Wait for Selector',
                            value:
                              typeof step.step.waitForSelector === 'boolean'
                                ? 'Yes'
                                : JSON.stringify(step.step.waitForSelector),
                          },
                        ]
                      : []),
                    ...(step.step.iframeContext?.autoDetect
                      ? [
                          {
                            label: 'Auto-detect Iframe',
                            value: 'Yes',
                          },
                        ]
                      : []),
                  ]}
                  columns={2}
                />
              </Panel>
            );
          })}
        </Collapse>
      </div>

      {data.mappings && (
        <div style={{ marginTop: 24 }}>
          <Title level={4}>{t('workflow.dynamicFieldMapping')}</Title>
          <Card className="shadow-lg" style={{ marginTop: 16 }}>
            <Descriptions bordered column={1}>
              <Descriptions.Item
                label={t('workflow.mappings.dataSource.label', { defaultValue: 'Data Source' })}
              >
                {data.mappings.dataSource}
              </Descriptions.Item>
              {data.mappings.fieldsMap && Object.keys(data.mappings.fieldsMap).length > 0 && (
                <Descriptions.Item
                  label={t('workflow.mappings.fields.title', {
                    defaultValue: 'Field Mappings',
                  })}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {Object.entries(data.mappings.fieldsMap).map(([stepOrder, applicationKey]) => (
                      <div key={stepOrder} style={{ marginBottom: 8 }}>
                        <Tag color="blue">Step {stepOrder}</Tag>
                        <span style={{ marginLeft: 8 }}>→</span>
                        <Tag color="green" style={{ marginLeft: 8 }}>
                          {formatLabel(applicationKey)}
                        </Tag>
                        <span style={{ marginLeft: 8, color: '#666', fontSize: '12px' }}>
                          ({applicationKey})
                        </span>
                      </div>
                    ))}
                  </Space>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </div>
      )}

      {data.condition &&
        Object.keys(data.condition).length > 0 &&
        (() => {
          const conditionEntries = Object.entries(data.condition || {});
          const lastIndex = conditionEntries.length - 1;

          return (
            <div style={{ marginTop: 24 }}>
              <Title level={4}>
                {t('workflow.conditions.sectionTitle', {
                  defaultValue: 'Workflow Conditions',
                })}
              </Title>
              <Card className="shadow-lg" style={{ marginTop: 16 }}>
                <Descriptions bordered column={1}>
                  {conditionEntries.map(([key, value], index) => (
                    <Descriptions.Item
                      key={key}
                      label={t('workflow.conditions.itemTitle', {
                        defaultValue: 'Condition {{index}}',
                        index: index + 1,
                      })}
                    >
                      {formatConditionLabel(key)}
                      {key === WORKFLOW_CONDITION_SKIP_RE_LOGIN &&
                        value &&
                        typeof value === 'object' &&
                        !Array.isArray(value) && (
                          <span>
                            {' '}
                            (
                            {t('workflow.conditions.skipReLogin.loginCheckStep.label', {
                              defaultValue: 'Login Check Step',
                            })}
                            : {(value as any)?.login_check_step || 'N/A'},{' '}
                            {t('workflow.conditions.skipReLogin.jumpToStep.label', {
                              defaultValue: 'Jump to Step',
                            })}
                            : {(value as any)?.jump_to_step || 'N/A'})
                          </span>
                        )}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </Card>
            </div>
          );
        })()}
    </>
  );
};

export default WorkflowDetailsComponent;
